// SPEC-31 · the app's language, held above the routes. A tap on a segment
// changes it at once: every screen reads its words through useCopy() at
// render, so nothing reloads and nothing is lost (the tab, the scroll, a
// questionnaire in progress). The device remembers the choice; the account
// gets it as soon as there is one, through the AccountLanguage seam.
//
// At a sign-in, and at each launch with a session (SPEC-52: the Splash),
// the account's language wins, so one the team set from the admin applies
// at her next opening; an account with none yet gets the device's. The
// last write wins: a choice she makes afterwards goes to the account. A
// save that fails leaves the language on this device, says so on Perfil,
// and is tried again at the next launch or when the network comes back;
// until it lands, the account's older language does not replace it.
// `<html lang>` follows the language.
//
// Without a provider (a screen rendered alone in a test) the app reads PT.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { codeOf } from "../account/firebaseError";
import type { AccountLanguage } from "../settings/settings";
import { HTML_LANG, copyFor, initialLanguage, markUnsaved, storeLanguage, unsavedChoice } from "./language";
import type { Copy, Language } from "./language";

export interface LanguageState {
  readonly language: Language;
  /** Every word of the app in that language. */
  readonly copy: Copy;
  /** Her tap on a segment: applies at once, stays on this device, and goes to her account when she has one. */
  readonly setLanguage: (language: Language) => void;
  /**
   * After a sign-in, and at a launch with a session: the account's language wins; an account without one
   * gets the device's. A choice made here that has not reached the account yet stays.
   */
  readonly syncWithAccount: () => void;
  /** After Criar conta: the new account gets the language chosen on the device. */
  readonly saveToAccount: () => void;
  /** The last save to her account failed; the language holds on this device. */
  readonly saveFailed: boolean;
}

const PT: LanguageState = {
  language: "pt",
  copy: copyFor("pt"),
  setLanguage: () => {},
  syncWithAccount: () => {},
  saveToAccount: () => {},
  saveFailed: false,
};

const LanguageContext = createContext<LanguageState>(PT);

export function LanguageProvider({ account, children }: { account: AccountLanguage; children: ReactNode }) {
  const [language, setShown] = useState<Language>(initialLanguage);
  const [saveFailed, setSaveFailed] = useState(unsavedChoice);
  /** What the async steps read between renders: the language shown, and the one the account should end with. */
  const current = useRef(language);
  const wanted = useRef(language);
  /** Bumped by each tap, so an account read that lands after it never undoes her choice. */
  const taps = useRef(0);
  /** One save at a time, in the order she chose: a slow first save never lands after a later one. */
  const queue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    document.documentElement.lang = HTML_LANG[language];
    // Back to the page's own language (index.html's) when the provider leaves.
    return () => {
      document.documentElement.lang = HTML_LANG.pt;
    };
  }, [language]);

  const show = useCallback((next: Language) => {
    current.current = next;
    setShown(next);
    storeLanguage(next);
  }, []);

  /** Sends the language to the signed-in account; with no one signed in, the device's memory is enough. */
  const save = useCallback(
    (next: Language) => {
      wanted.current = next;
      queue.current = queue.current.then(async () => {
        // A later choice is queued behind this one: only the last one is sent.
        if (wanted.current !== next) return;
        try {
          if ((await account.accountId()) !== null) await account.save(next);
          markUnsaved(false);
          setSaveFailed(false);
        } catch (error) {
          // The code only, as every write of the app logs it.
          console.error("[language] not saved to the account", { code: codeOf(error) });
          markUnsaved(true);
          setSaveFailed(true);
        }
      });
    },
    [account],
  );

  const setLanguage = useCallback(
    (next: Language) => {
      if (next === current.current) return;
      taps.current += 1;
      show(next);
      save(next);
    },
    [save, show],
  );

  const syncWithAccount = useCallback(() => {
    // A choice made here has not reached the account yet (offline, a failed save): it is the newest, so the
    // account's older language must not undo it. The retry at launch and when the network is back sends it.
    if (unsavedChoice()) return;
    const tapsBefore = taps.current;
    void (async () => {
      let stored: Language | null;
      try {
        const accountId = await account.accountId();
        if (accountId === null) return;
        stored = await account.read(accountId);
      } catch (error) {
        // Unread: the device's language stays, and nothing is written over the account's.
        console.error("[language] the account's language was not read", { code: codeOf(error) });
        return;
      }
      // She tapped a segment meanwhile: her choice is the one on its way to the account.
      if (taps.current !== tapsBefore) return;
      if (stored === null) {
        save(current.current);
        return;
      }
      wanted.current = stored;
      markUnsaved(false);
      setSaveFailed(false);
      show(stored);
    })();
  }, [account, save, show]);

  const saveToAccount = useCallback(() => save(current.current), [save]);

  // A choice that did not reach the account is sent again: now, then whenever the network comes back.
  useEffect(() => {
    const retry = () => {
      if (unsavedChoice()) save(current.current);
    };
    retry();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [save]);

  const state = useMemo<LanguageState>(
    () => ({ language, copy: copyFor(language), setLanguage, syncWithAccount, saveToAccount, saveFailed }),
    [language, setLanguage, syncWithAccount, saveToAccount, saveFailed],
  );
  return <LanguageContext.Provider value={state}>{children}</LanguageContext.Provider>;
}

/** The language, the way to change it, and how its last save to the account went. */
export const useLanguage = (): LanguageState => useContext(LanguageContext);

/** Every word of the app in the current language. A screen reads it at render, never once at module level. */
export const useCopy = (): Copy => useContext(LanguageContext).copy;

/**
 * The class that fades a screen's words in after a language change: the
 * tab's 120 ms fade (--dur-micro, --ease-pyf), opacity only, so reduced
 * motion has no movement to drop and keeps it, as it keeps every fade of
 * the design system. Empty until the language changes while the screen is
 * shown; two class names, so a second change restarts the fade.
 */
export function useLanguageFade(): string {
  const { language } = useLanguage();
  const [seen, setSeen] = useState(language);
  const [changes, setChanges] = useState(0);
  if (seen !== language) {
    setSeen(language);
    setChanges((count) => count + 1);
  }
  if (changes === 0) return "";
  return changes % 2 === 1 ? "language-fade" : "language-fade-again";
}
